
// Third-party imports
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
	zip as observableZip,
	concat as observableConcat,
	Observable,
	PartialObserver,
	Subscription,
	BehaviorSubject
} from 'rxjs';
import {
	scan,
	toArray,
	share,
	retryWhen,
	shareReplay
} from 'rxjs/operators';
import * as _ from "lodash";
import * as moment from "moment";

// Application imports
import { CredentialsCache } from '../../../common';
import { Exception } from '../../../app.services/exception';
import { IURIParameter } from '../interfaces/iuriparameter';
import { LogManager } from '../../log/services/log.manager';
import { LogCat } from '../../log/services/log.manager.categories';
import { Serialization } from "../utils/serialization";
import { HttpDataPovider } from "./user.ctx.token";

// const NO_CONTENT_CODE = 204;
const NOT_FOUND_CODE = 404;
// const INTERNAL_ERROR_CODE = 500;
const UNAUTHORIZED_CODE = 401;
const FORBIDDEN_CODE = 403;
const CUSTOM_UNAUTHORIZED_CODE = 430;

// this is not done automatically so we dont damage existing code
export function convertMapToObj(map){
	const obj = {};
	for (let [k,v] of map) {
		obj[k] = v
	}
	return obj;
}


let propraietrayToJson =function() {
	return moment(this).format('YYYY-MM-DD\\THH:mm:ss.SSS');
};

export function forceDateSerializationFormat() {
	if (Date.prototype.toJSON!==propraietrayToJson){
		Date.prototype.toJSON=propraietrayToJson;
	}
}

export class HttpObservable<T> extends Observable<any> {
	subscribe(observerOrNext?:PartialObserver<T> | ((value:T) => void),
											error?:(error:Exception) => void, complete?:() => void):Subscription {
		/* tslint:disable */
		return this.subscribe(observerOrNext, error, complete);
		/* tslint:enable */
	}
}

// array of large objects
export class LargeObjectArray {
	content: Array<Blob|string>;
}

/**
	* a helper utility class for performing REST against our server.
	* the class assumes we are working against a server with our
	* standards - including authentication details, URI standards etc.
	*/
export class HttpWrapper {
	public retryGetDefaults = 3; //todo - get from configuration?
	public retryDetalySec = 1; //todo - get from configuration?
	public maxUrlLength = 2000; //todo - get from configuration?

	private cachedObservables:Map<string,Observable<any>>;

	constructor(private http:HttpClient, private tokenProvider:CredentialsCache, private baseUrl:string
		, private httpData:HttpDataPovider) {

	}


	/**
		* perform a put request to the server
		* @param ctrl name of controller to access on the server
		* @param func name of function to invoke on the server (including "put" prefix)
		* @param content data to send as part of the reuqest header
		* @param p URI parameters is required
		*/
	public put(ctrl:string, func:string, content:any, p?:IURIParameter | IURIParameter[],responseType="json",addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, false, 'PUT',responseType,false,addViewMode);
	}

	/**
		* perform a post request to the server
		* @param ctrl name of controller to access on the server
		* @param func name of function to invoke on the server (including "put" prefix)
		* @param content data to send as part of the reuqest header
		* @param p URI parameters is required
		*/
	public post(ctrl:string, func:string, content:any, p?:IURIParameter | IURIParameter[],addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, false, 'POST','json',false,addViewMode);
	}

	/**
	 * perform a delete request to the server
	 * @param ctrl name of controller to access on the server
	 * @param func name of function to invoke on the server (including "put" prefix)
	 * @param p URI parameters is required
	 */
	public delete(ctrl:string, func:string, p:IURIParameter | IURIParameter[],addViewMode=false) {
		return this.invoke(ctrl, func, p,null, 0, false, 'DELETE','json',false,addViewMode);
	}

	/**
		* perform a get request from the server
		* @param ctrl name controller to access on the server
		* @param func name of function to access on the server (full name - including "get" prefix if required)
		* @param p parameter(s) to send
		* @param retries number of retries if the request fails
	 	* @param forCache indicate true if you're going to use observable based caching for this observable and want to disble the inner share()
		*/
	public get(ctrl:string, func:string, p?:IURIParameter | IURIParameter[], retries?:number, allowCaching?:boolean,addViewMode=false,forCache=false) {
		return this.invoke(ctrl, func, p ? p : [], null, retries != null ? retries : this.retryGetDefaults, false,
			'GET','json', allowCaching,addViewMode,!forCache);
	}

	public getBlob(ctrl:string, func:string, p?:IURIParameter | IURIParameter[], retries?:number,
					anon:boolean = false, cache:boolean = false,addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], null, retries ? retries : this.retryGetDefaults, anon,
			'GET', 'blob', cache,addViewMode);
	}

	public putBlobData(ctrl: string, func: string, content: Blob, p?: IURIParameter | IURIParameter[], anon: boolean = false,addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, anon,
			'PUT', undefined, false,addViewMode);
	}

	public putBlob(ctrl: string, func: string, content: any, p?: IURIParameter | IURIParameter[], anon: boolean = false,addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, anon,
			'PUT', 'blob', false,addViewMode); //notice put instead of PUT (is it a bug?)
	}

	public putBlobWithMultipleResponseType(ctrl: string, func: string, content: any, p?: IURIParameter | IURIParameter[], anon: boolean = false,addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, anon,
			'PUT', 'application/octet-stream,application/json', false,addViewMode); //notice put instead of PUT (is it a bug?)
	}

	public putLargeObjects(ctrl: string, func: string, content: LargeObjectArray, p?: IURIParameter | IURIParameter[], anon: boolean = false,addViewMode=false) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, anon,
			'PUT', undefined, false,addViewMode);
	}

	/**
	 * Performs one or more requests to the server, based on maximum URL limitation.
	 * @param ctrl name controller to access on the server
	 * @param func name of function to access on the server (full name - including "get" prefix if required)
	 * @param p one parameter which is an array that can be splitted if needed
	 * @param parallel whether to run the request in parallel (true) or sequential (false)
	 * @returns {Observable<T[]>}
	 */
	public getSplitted(ctrl:string, func:string, p:IURIParameter, parallel:boolean, anon?:boolean) {
		let url = this.getUrl(ctrl, func, p);
		let numOfRequests = Math.ceil(url.length / this.maxUrlLength);

		let splitedRequests:any[][];

		do {
			if (numOfRequests > p.pVal.length) {
				LogManager.error(LogCat.Comm.Proxies, "too long url parameter", p);
				throw new Error("too long url parameter");
			}

			splitedRequests = _.chunk(p.pVal, Math.ceil(p.pVal.length / numOfRequests));

			if (!splitedRequests.every((currRequest) => {
					url = this.getUrl(ctrl, func, {pName: p.pName, pVal: currRequest});

					return (url.length <= this.maxUrlLength);
				})) {
				++numOfRequests;
			} else {
				break;
			}
		// eslint-disable-next-line no-constant-condition
		} while (true);

		let observables = splitedRequests.map(item =>anon?this.getAnon(ctrl, func, {pName: p.pName, pVal: item}):
														this.get(ctrl, func, {pName: p.pName, pVal: item}));

		if (!parallel) {
			return observableConcat(...observables).pipe(toArray());
		}

		return observableZip(...observables);
	}

	/**
		* perform an ANNONIMIZED get request from the server (do not attach user credentials)
		* @param ctrl name controller to access on the server
		* @param func name of function to access on the server (full name - including "get" prefix if required)
		* @param p parameter(s) to send
		* @param retries number of retries if the request fails
		*/
	public getAnon(ctrl:string, func:string, p?:IURIParameter | IURIParameter[], retries?:number, allowCaching?:boolean) {
		return this.invoke(ctrl, func, p ? p : [], null, retries ? retries : this.retryGetDefaults, true, 'GET','json',true,false);
	}

	public postAnon(ctrl:string, func:string, content:any, p?:IURIParameter | IURIParameter[]) {
		return this.invoke(ctrl, func, p ? p : [], content, 0, true, 'POST',false,false,false);
	}

	/**
		* helper function to perform date construction for date objects.
		* @param raw object to process
		* @param regExp regular expression for names of keys and sub-keys to evaluate
		*/
	public parseDates(raw:any, regExp?:RegExp):any {
		let pkg = LogCat.Comm.Proxies;
		let startTime:number;
		if (LogManager.isInfoEnabled(pkg)) {
			LogManager.info(LogCat.Comm.Proxies, "before date parsing");
			startTime = Date.now();
		}
		let ret = Serialization.convertDateStringsToDates(raw, regExp);
		if (LogManager.isInfoEnabled(pkg)) {
			let elapased = Date.now() - startTime;
			LogManager.info(pkg, "after date parsing. elapsed time -", elapased);
		}
		return ret;
	}

	public getIframeUrl(ctrl:string, func:string, p:IURIParameter[])  {
		let params = [...p, {pName:'token', pVal:this.tokenProvider.accessToken}];
		return this.getUrl(ctrl, func, params);
	}

	public getTokenUrl(){
		return "token="+this.tokenProvider.accessToken;
	}

	public invoke(ctrl:string, func:string, p:IURIParameter | IURIParameter[], content:any, retries:number,
					anon:Boolean, method:string, responseType:any = 'json', cache:Boolean = false, addViewModel:boolean,addShare=true) : Observable<any> {
		let headers = new HttpHeaders();

		// Our server currently does not support xml. Until we enable the server side (if we really want to)
		if (responseType === 'json') {
			headers = headers.set('Accept', 'application/json');
		}

		switch (method) {
			case 'GET':
				// override ie caching
				if (!cache) {
					headers = headers.append("Cache-Control", 'no-cache');
					headers = headers.append("Pragma", 'no-cache');
				}
				break;
			case 'POST':
			case 'PUT':
				if (content instanceof LargeObjectArray) {
					let formData:FormData = new FormData();
					content.content.forEach(s => formData.append('uploadFile', s));

					content = formData;
				} else if ((content instanceof Blob) || (content instanceof File)){
					let formData:FormData = new FormData();
					formData.append('uploadFile', content);

					content = formData;
				} else {
					forceDateSerializationFormat();
					headers = headers.append('Content-Type', 'application/json');
					content = content ? JSON.stringify(content) : null;
				}
				break;
		}

		if (!anon) {
			headers = headers.set("Authorization", "Bearer " + this.tokenProvider.accessToken);
		}

		let retVal= this.http.request(method, this.getUrl(ctrl, func, p,addViewModel), {
			body: content ? content : null,
			headers, responseType
		});
		if (addShare){
			// add share is problematic in cases where we want to later publishLast since it might not close the obserable
			retVal=retVal.pipe(share());// especially critical for operations sending stuff to the server;
		}
		retVal=retVal.pipe(
			retryWhen((errors) => {
				return errors.pipe(scan(function (errorCount:number, err:any) {

					if (err.status === NOT_FOUND_CODE || errorCount >= retries) {
						if (err.status === NOT_FOUND_CODE) {
							LogManager.debug(LogCat.Comm.Proxies, "not found error",err);
						} else {
							LogManager.error(LogCat.Comm.Proxies, "unexpected http error",err);
						}

						throw err;
					}
					//  if the error is a CUSTOM_UNAUTHORIZED_CODE (430), we don't want to retry
					if (err.status === CUSTOM_UNAUTHORIZED_CODE) {
						// error has been logged by the http interceptor
						//LogManager.debug(LogCat.Comm.Proxies, "unauthorized error", err);
						throw err;
					}
					return errorCount + 1;
				}, 0));
			}));
		return retVal;
	}


	public getUrl(ctrl:string, func:string, p:IURIParameter | IURIParameter[],addUserGroup?:boolean) : string {
		let arrParams:IURIParameter[];
		if (!p){
			arrParams=[];
		}else if (!Array.isArray(p)) {
			arrParams=[p];
		}else{
			arrParams=p;
		}

		if (this.httpData && addUserGroup) {
			arrParams.push(...Object.getOwnPropertyNames(this.httpData.keys).map(key => {
				return {pName: key, pVal: this.httpData.keys[key]};
			}));
		}

		let paramsStr = Serialization.toURLString(arrParams);
		paramsStr = paramsStr ? ("?" + paramsStr) : paramsStr;

		return (this.baseUrl + "/" + ctrl + "/" + func + paramsStr);
	}

	public clearCache(cacheKey:string){
		if (this.cachedObservables) {
			this.cachedObservables.delete(cacheKey);
		}
	}
	public wrappWithCache(cacheKey:string,httpObservable:Observable<any>){
		if (!this.cachedObservables){
			this.cachedObservables= new Map<string, Observable<any>>();
		}
		if(this.cachedObservables.get(cacheKey)){
			return this.cachedObservables.get(cacheKey);
		}else {
			// note - the standard rxjs operators didnt work for caching since they all close the observable
			// after it has been complete and try to open it again later (this does not provide caching and in addition
			// it does not work because the authentication token in the request has already expired).

			const resultSubject = new BehaviorSubject<any>(null);

			httpObservable.subscribe(
				(result) => {
					resultSubject.next(result);
				}
			);

			let cachedValue=resultSubject.asObservable().pipe(shareReplay(1));
			this.cachedObservables.set(cacheKey, cachedValue);
			return httpObservable;
		}
	}

}
